import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Plus, Pencil, Trash2, UserCheck, Megaphone, Settings as Cog, Eye, EyeOff, MessageCircle, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fileUrl, POINT_CATS, waLink, approvalMsg, overdueMsg, brl, fmtDate } from "../lib/api";
import { Avatar, Empty, Field, Loader, PageHeader, PillTabs, Logo } from "../components/common";
import { ImageUpload } from "../components/ImageUpload";
import { Slider } from "../components/ui/slider";
import { AdsManager } from "../components/admin/AdsManager";
import { LoginTexts } from "../components/admin/LoginTexts";
import { LinkProfileButton } from "../components/admin/LinkProfile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_TXT = { pending: "Pendentes", approved: "Aprovados", rejected: "Recusados" };

const WaButton = ({ phone, text, testid, onSent, label = "WhatsApp" }) => {
  const href = waLink(phone, text);
  if (!href) return <span className="text-[11px] text-[#64748B]">Sem telefone</span>;
  return (
    <a data-testid={testid} href={href} target="_blank" rel="noreferrer" onClick={onSent}
      className="flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-black hover:bg-emerald-400">
      <MessageCircle className="h-4 w-4" />{label}
    </a>
  );
};

const Notices = () => {
  const { settings } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/notices").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const done = async (kind, id) => { await api.post("/admin/notices/done", { kind, id }); load(); };
  if (!d) return <Loader />;
  return (
    <div className="space-y-8">
      <div>
        <div className="h-display mb-1 text-2xl">Cadastros aprovados</div>
        <p className="mb-3 text-sm text-[#94A3B8]">Clique para abrir o WhatsApp com a mensagem pronta e depois em enviar.</p>
        {d.approvals.length === 0 ? <p className="text-sm text-[#64748B]">Nenhum aviso pendente.</p> : d.approvals.map((u) => (
          <div key={u.user_id} data-testid={`notice-approval-${u.user_id}`} className="card-x mb-2 flex flex-wrap items-center gap-3 p-3">
            <Avatar user={u} size={40} />
            <div className="min-w-0 flex-1"><Link to={`/app/atletas/${u.user_id}`} className="font-semibold hover:text-[#E4F222]">{u.name}</Link><div className="truncate text-xs text-[#94A3B8]">{u.phone || "Cadastre o telefone no perfil"}</div></div>
            <WaButton testid={`wa-approval-${u.user_id}`} phone={u.phone} text={approvalMsg(u.name)} onSent={() => setTimeout(() => done("approval", u.user_id), 500)} />
            <button data-testid={`dismiss-approval-${u.user_id}`} onClick={() => done("approval", u.user_id)} className="text-xs text-[#64748B] hover:text-white">Dispensar</button>
          </div>
        ))}
      </div>
      <div>
        <div className="h-display mb-1 text-2xl">Cobranças vencidas</div>
        {d.overdue.length === 0 ? <p className="text-sm text-[#64748B]">Nenhuma cobrança vencida sem aviso.</p> : d.overdue.map((c) => (
          <div key={c.id} data-testid={`notice-overdue-${c.id}`} className="card-x mb-2 flex flex-wrap items-center gap-3 p-3">
            <Avatar user={c.user} size={40} />
            <div className="min-w-0 flex-1"><div className="font-semibold">{c.user?.name}</div><div className="truncate text-xs text-[#94A3B8]">{c.description} · {brl(c.amount)} · venceu {fmtDate(c.due_date)}</div></div>
            <WaButton testid={`wa-overdue-${c.id}`} phone={c.user?.phone} text={overdueMsg(c, settings?.pix_key)} onSent={() => setTimeout(() => done("overdue", c.id), 500)} />
            <button data-testid={`dismiss-overdue-${c.id}`} onClick={() => done("overdue", c.id)} className="text-xs text-[#64748B] hover:text-white">Dispensar</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Approvals = () => {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/users", { params: { status } }).then((r) => setList(r.data));
  useEffect(() => { setList(null); load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const setSt = async (u, st) => {
    try { await api.post(`/admin/users/${u.user_id}/status`, { status: st }); toast.success(st === "approved" ? `${u.name} aprovado` : "Status atualizado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PillTabs testid="approval-filter" tabs={Object.entries(STATUS_TXT).map(([value, label]) => ({ value, label }))} value={status} onChange={setStatus} />
      <div className="mt-4">
        {!list ? <Loader /> : list.length === 0 ? <Empty icon={UserCheck} title="Ninguém aqui" text={status === "pending" ? "Nenhum cadastro aguardando aprovação." : "Lista vazia."} /> : (
          <div className="space-y-2">
            {list.map((u) => (
              <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="card-x flex flex-wrap items-center gap-3 p-3">
                <Avatar user={u} size={42} />
                <div className="min-w-0 flex-1">
                  <Link to={`/app/atletas/${u.user_id}`} className="font-semibold hover:text-[#E4F222]">{u.name}</Link>
                  <div className="truncate text-xs text-[#94A3B8]">{u.email} · {u.role === "admin" ? "Admin" : "Atleta"} · {new Date(u.created_at).toLocaleDateString("pt-BR")}</div>
                  {u.onboarded && <div className="truncate text-xs text-[#64748B]">{[u.phone, u.ct_name, u.level, u.court_side, u.play_time, u.training === true ? "Treinando" : u.training === false ? "Não treina" : ""].filter(Boolean).join(" · ")}</div>}
                </div>
                {u.role !== "admin" && (
                  <div className="flex flex-wrap gap-2">
                    {status === "approved" && <WaButton testid={`wa-approved-${u.user_id}`} phone={u.phone} text={approvalMsg(u.name)} label="Avisar" />}
                    {status !== "approved" && <LinkProfileButton user={u} onDone={load} />}
                    {status !== "approved" && <button data-testid={`approve-${u.user_id}`} onClick={() => setSt(u, "approved")} className="btn-lime flex items-center gap-1 rounded-full px-4 py-2 text-sm"><Check className="h-4 w-4" />Aprovar como novo</button>}
                    {status !== "rejected" && <button data-testid={`reject-${u.user_id}`} onClick={() => setSt(u, "rejected")} className="btn-ghost flex items-center gap-1 text-red-400"><X className="h-4 w-4" />{status === "approved" ? "Bloquear" : "Recusar"}</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SponsorForm = ({ open, onOpenChange, sponsor, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(sponsor || { name: "", image_path: null, link: "", description: "", active: true, order: 0 }); }, [open, sponsor]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, order: Number(f.order || 0) };
      if (sponsor) await api.put(`/sponsors/${sponsor.id}`, body); else await api.post("/sponsors", body);
      toast.success("Patrocinador salvo"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">{sponsor ? "Editar patrocinador" : "Novo patrocinador"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Logo / banner"><ImageUpload wide value={f.image_path} onChange={(v) => setF({ ...f, image_path: v })} testid="sponsor-image-upload" label="Enviar imagem" /></Field>
          <Field label="Nome"><input required data-testid="sponsor-name-input" className="inp" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Link (site/Instagram)"><input data-testid="sponsor-link-input" className="inp" value={f.link || ""} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="https://" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ordem"><input type="number" data-testid="sponsor-order-input" className="inp" value={f.order ?? 0} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
            <Field label="Exibir">
              <select data-testid="sponsor-active-select" className="inp" value={f.active ? "1" : "0"} onChange={(e) => setF({ ...f, active: e.target.value === "1" })}><option value="1">Ativo</option><option value="0">Oculto</option></select>
            </Field>
          </div>
          <button data-testid="sponsor-save-btn" className="btn-lime w-full rounded-full py-3">Salvar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Sponsors = () => {
  const [list, setList] = useState(null);
  const [dlg, setDlg] = useState({ open: false, sponsor: null });
  const load = () => api.get("/sponsors", { params: { all: true } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (s) => { if (!window.confirm(`Excluir ${s.name}?`)) return; await api.delete(`/sponsors/${s.id}`); load(); };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[#94A3B8]">Exibidos na tela de entrada e no início do app.</p>
        <button data-testid="new-sponsor-btn" onClick={() => setDlg({ open: true, sponsor: null })} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Adicionar</button>
      </div>
      {!list ? <Loader /> : list.length === 0 ? <Empty icon={Megaphone} title="Sem patrocinadores" /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => (
            <div key={s.id} data-testid={`sponsor-row-${s.id}`} className="card-x overflow-hidden">
              <div className="flex h-28 items-center justify-center bg-[#0B0E14] p-3">
                {s.image_path ? <img src={fileUrl(s.image_path)} alt={s.name} className="max-h-full object-contain" /> : <span className="h-display text-2xl">{s.name}</span>}
              </div>
              <div className="flex items-center gap-2 p-3">
                <span className="flex-1 truncate text-sm font-semibold">{s.name}</span>
                {s.active ? <Eye className="h-4 w-4 text-[#E4F222]" /> : <EyeOff className="h-4 w-4 text-[#64748B]" />}
                <button data-testid={`edit-sponsor-${s.id}`} onClick={() => setDlg({ open: true, sponsor: s })} className="p-1.5 text-[#94A3B8]"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-sponsor-${s.id}`} onClick={() => del(s)} className="p-1.5 text-[#94A3B8] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <SponsorForm open={dlg.open} sponsor={dlg.sponsor} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </div>
  );
};

const SettingsForm = () => {
  const { settings, loadSettings } = useAuth();
  const [f, setF] = useState(settings);
  useEffect(() => { setF(settings); }, [settings]);
  if (!f) return <Loader />;
  const save = async () => {
    try {
      const points = Object.fromEntries(Object.entries(f.points).map(([k, v]) => [k, Number(v || 0)]));
      await api.put("/settings", { app_name: f.app_name, logo_path: f.logo_path, icon_path: f.icon_path, pix_key: f.pix_key, pix_name: f.pix_name, welcome_text: f.welcome_text, points,
        logo_size_login: f.logo_size_login, logo_size_app: f.logo_size_app, login_video_path: f.login_video_path });
      await loadSettings();
      toast.success("Configurações salvas");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="card-x space-y-4 p-5">
        <div className="h-display text-2xl">Identidade</div>
        <Field label="Nome do app"><input data-testid="settings-name-input" className="inp" value={f.app_name} onChange={(e) => setF({ ...f, app_name: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Logo do app"><ImageUpload value={f.logo_path} onChange={(v) => setF({ ...f, logo_path: v })} testid="settings-logo-upload" label="Logo" /></Field>
          <Field label="Ícone (favicon)"><ImageUpload value={f.icon_path} onChange={(v) => setF({ ...f, icon_path: v })} testid="settings-icon-upload" label="Ícone" /></Field>
        </div>
        <Field label="Texto de boas-vindas"><textarea data-testid="settings-welcome-input" rows={2} className="inp" value={f.welcome_text} onChange={(e) => setF({ ...f, welcome_text: e.target.value })} /></Field>
        {[["logo_size_login", "Tamanho da logo na tela de login", 40, 220, "login"], ["logo_size_app", "Tamanho da logo no topo do app", 24, 140, "app"]].map(([k, l, min, max, w]) => (
          <Field key={k} label={`${l} · ${f[k]}px`}>
            <Slider data-testid={`slider-${k}`} min={min} max={max} step={2} value={[f[k] || min]} onValueChange={([v]) => setF({ ...f, [k]: v })} className="py-2" />
            <div className="mt-2 flex min-h-[60px] items-center overflow-hidden rounded-xl border border-[#232B3E] bg-[#0B0E14] p-3" data-testid={`preview-${k}`}>
              <Logo where={w} size={f[k]} path={f.logo_path} />
            </div>
          </Field>
        ))}
        <Field label="Vídeo de fundo da tela de login (MP4, até 80MB)">
          <ImageUpload video wide value={f.login_video_path} onChange={(v) => setF({ ...f, login_video_path: v })} testid="settings-video-upload" label="Enviar vídeo" />
        </Field>
      </div>
      <div className="space-y-6">
        <div className="card-x space-y-4 p-5">
          <div className="h-display text-2xl">PIX</div>
          <Field label="Chave PIX"><input data-testid="settings-pix-input" className="inp" value={f.pix_key} onChange={(e) => setF({ ...f, pix_key: e.target.value })} /></Field>
          <Field label="Nome do recebedor"><input data-testid="settings-pixname-input" className="inp" value={f.pix_name} onChange={(e) => setF({ ...f, pix_name: e.target.value })} /></Field>
        </div>
        <div className="card-x space-y-3 p-5">
          <div className="h-display text-2xl">Pontuação padrão</div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(POINT_CATS).map(([k, l]) => (
              <Field key={k} label={l}><input type="number" data-testid={`settings-points-${k}`} className="inp" value={f.points?.[k] ?? 0} onChange={(e) => setF({ ...f, points: { ...f.points, [k]: e.target.value } })} /></Field>
            ))}
          </div>
        </div>
      </div>
      <button data-testid="settings-save-btn" onClick={save} className="btn-lime rounded-full py-3 lg:col-span-2">Salvar configurações</button>
    </div>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("aprovacoes");
  return (
    <div>
      <PageHeader eyebrow="Painel" title="Administração" />
      <div className="no-scrollbar mb-6 flex gap-6 overflow-x-auto border-b border-[#232B3E]">
        {[["aprovacoes", "Aprovações", UserCheck], ["avisos", "Avisos WhatsApp", BellRing], ["patrocinadores", "Patrocinadores", Megaphone], ["publicidade", "Publicidade", Megaphone], ["textos", "Textos do login", Pencil], ["config", "Configurações", Cog]].map(([v, l, I]) => (
          <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-3 text-sm font-semibold ${tab === v ? "border-[#E4F222] text-white" : "border-transparent text-[#64748B]"}`}>
            <I className="h-4 w-4" /><span className="hidden sm:inline">{l}</span><span className="sm:hidden">{l.split(" ")[0].slice(0, 9)}</span>
          </button>
        ))}
      </div>
      {tab === "aprovacoes" && <Approvals />}
      {tab === "avisos" && <Notices />}
      {tab === "patrocinadores" && <Sponsors />}
      {tab === "publicidade" && <AdsManager />}
      {tab === "textos" && <LoginTexts />}
      {tab === "config" && <SettingsForm />}
    </div>
  );
}
